import React, { Component } from 'react';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';

class Subscriptions extends Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      subscriptions: [],
      loading: true,
      searchQuery: '',
      editingSubscription: null,
      showPreferencesModal: false,
    };
  }

  async componentDidMount() {
    await this.fetchSubscriptions();
  }

  fetchSubscriptions = async () => {
    try {
      const response = await api.get('/subscriptions');
      this.setState({
        subscriptions: response.data.subscriptions,
        loading: false,
      });
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      this.setState({ loading: false });
    }
  };

  handleUnwatch = async (jobId) => {
    if (!window.confirm('Stop watching this job? You will no longer receive notifications.')) {
      return;
    }

    try {
      await api.delete(`/subscriptions/job/${jobId}`);
      await this.fetchSubscriptions();
    } catch (error) {
      console.error('Failed to unwatch job:', error);
    }
  };

  openPreferencesModal = (subscription) => {
    this.setState({
      editingSubscription: subscription,
      showPreferencesModal: true,
    });
  };

  closePreferencesModal = () => {
    this.setState({
      editingSubscription: null,
      showPreferencesModal: false,
    });
  };

  handleUpdatePreferences = async (preferences) => {
    const { editingSubscription } = this.state;

    try {
      await api.put(`/subscriptions/job/${editingSubscription.jobId}`, preferences);
      await this.fetchSubscriptions();
      this.closePreferencesModal();
    } catch (error) {
      console.error('Failed to update preferences:', error);
    }
  };

  getFilteredSubscriptions = () => {
    const { subscriptions, searchQuery } = this.state;
    
    if (!searchQuery) {
      return subscriptions;
    }

    return subscriptions.filter(sub =>
      sub.jobName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.ownerName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  render() {
    const { loading, searchQuery, showPreferencesModal, editingSubscription } = this.state;
    const filteredSubscriptions = this.getFilteredSubscriptions();

    if (loading) {
      return (
        <div className="min-h-screen bg-slate-50">
          <Navbar />
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner size="large" />
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Job Subscriptions</h1>
            <p className="text-slate-600 mt-1">
              Manage which jobs you're watching and customize notification preferences
            </p>
          </div>

          {/* Search and Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Search subscriptions..."
                    value={searchQuery}
                    onChange={(e) => this.setState({ searchQuery: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div className="ml-4">
                  <div className="text-sm text-slate-600">
                    Watching <span className="font-semibold text-slate-900">{filteredSubscriptions.length}</span> job{filteredSubscriptions.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Subscriptions List */}
            <div className="divide-y divide-slate-200">
              {filteredSubscriptions.length === 0 ? (
                <div className="px-6 py-16 text-center">
                  <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <p className="text-slate-600 text-lg mb-2">No subscriptions yet</p>
                  <p className="text-slate-500 text-sm">
                    {searchQuery ? 'No subscriptions match your search' : 'Click the watch button on jobs to get notifications'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => window.location.href = '/jobs'}
                      className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
                    >
                      Browse Jobs
                    </button>
                  )}
                </div>
              ) : (
                filteredSubscriptions.map((subscription) => (
                  <div key={subscription.id} className="px-6 py-4 hover:bg-slate-50 transition">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-sm font-semibold text-slate-900">{subscription.jobName}</h3>
                          {subscription.isOwner && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Your job
                            </span>
                          )}
                          {subscription.autoSubscribed && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              Auto-watched
                            </span>
                          )}
                          {subscription.jobArchived && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                              Archived
                            </span>
                          )}
                        </div>
                        {!subscription.isOwner && (
                          <p className="text-xs text-slate-500 mt-1">Owner: {subscription.ownerName}</p>
                        )}
                        <div className="flex items-center space-x-4 mt-2">
                          {subscription.notifyOnUpdate && (
                            <span className="text-xs text-slate-600 flex items-center">
                              <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Updates
                            </span>
                          )}
                          {subscription.notifyOnShare && (
                            <span className="text-xs text-slate-600 flex items-center">
                              <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Shares
                            </span>
                          )}
                          {subscription.notifyOnDelete && (
                            <span className="text-xs text-slate-600 flex items-center">
                              <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Deletes
                            </span>
                          )}
                          {subscription.notifyOnArchive && (
                            <span className="text-xs text-slate-600 flex items-center">
                              <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Archives
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => this.openPreferencesModal(subscription)}
                          className="px-3 py-1 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition"
                        >
                          Customize
                        </button>
                        <button
                          onClick={() => this.handleUnwatch(subscription.jobId)}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition"
                        >
                          Unwatch
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </main>

        {/* Preferences Modal */}
        {showPreferencesModal && editingSubscription && (
          <PreferencesModal
            subscription={editingSubscription}
            onClose={this.closePreferencesModal}
            onSave={this.handleUpdatePreferences}
          />
        )}
      </div>
    );
  }
}

// Preferences Modal Component
class PreferencesModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      notifyOnUpdate: props.subscription.notifyOnUpdate,
      notifyOnShare: props.subscription.notifyOnShare,
      notifyOnDelete: props.subscription.notifyOnDelete,
      notifyOnArchive: props.subscription.notifyOnArchive,
    };
  }

  handleSave = () => {
    const { notifyOnUpdate, notifyOnShare, notifyOnDelete, notifyOnArchive } = this.state;
    
    this.props.onSave({
      notify_on_update: notifyOnUpdate,
      notify_on_share: notifyOnShare,
      notify_on_delete: notifyOnDelete,
      notify_on_archive: notifyOnArchive,
    });
  };

  render() {
    const { subscription, onClose } = this.props;
    const { notifyOnUpdate, notifyOnShare, notifyOnDelete, notifyOnArchive } = this.state;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900">Notification Preferences</h2>
            <p className="text-sm text-slate-600 mt-1">{subscription.jobName}</p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-slate-600">Choose which notifications you want to receive for this job:</p>

            {/* Update Notifications */}
            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition">
              <div>
                <p className="text-sm font-medium text-slate-900">Job Updates</p>
                <p className="text-xs text-slate-500">When the job is modified</p>
              </div>
              <input
                type="checkbox"
                checked={notifyOnUpdate}
                onChange={(e) => this.setState({ notifyOnUpdate: e.target.checked })}
                className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
              />
            </label>

            {/* Share Notifications */}
            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition">
              <div>
                <p className="text-sm font-medium text-slate-900">Share Events</p>
                <p className="text-xs text-slate-500">When the job is shared with others</p>
              </div>
              <input
                type="checkbox"
                checked={notifyOnShare}
                onChange={(e) => this.setState({ notifyOnShare: e.target.checked })}
                className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
              />
            </label>

            {/* Delete Notifications */}
            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition">
              <div>
                <p className="text-sm font-medium text-slate-900">Deletion</p>
                <p className="text-xs text-slate-500">When the job is deleted</p>
              </div>
              <input
                type="checkbox"
                checked={notifyOnDelete}
                onChange={(e) => this.setState({ notifyOnDelete: e.target.checked })}
                className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
              />
            </label>

            {/* Archive Notifications */}
            <label className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition">
              <div>
                <p className="text-sm font-medium text-slate-900">Archive Events</p>
                <p className="text-xs text-slate-500">When the job is archived or unarchived</p>
              </div>
              <input
                type="checkbox"
                checked={notifyOnArchive}
                onChange={(e) => this.setState({ notifyOnArchive: e.target.checked })}
                className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
              />
            </label>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              onClick={this.handleSave}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default Subscriptions;